import { select, all, call, put, race, take, takeLatest } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import { parse } from 'tldjs'
import { delay } from 'redux-saga/effects'
import agent, { mocked } from '../../agent'
import appMode from 'helpers/getAppMode'
import { isDomainValid } from 'helpers/validate'

import * as actions from '../actions/backlinkChecker.actions'
import * as searchActions from '../actions/search.actions'
import * as appUsageActions from '../actions/appUsage.actions'

function convertBacklinkURL(value) {
  let url = value
  if (!value.includes('://')) {
    url = `https://${value}`
  }
  const parsedUrl = new URL(url)
  if (parsedUrl?.pathname?.length > 1) {
    return url
  }

  return parse(value).domain
}

function* pollGetResults() {
  try {
    let complete = false
    let strikes = 20
    const state = yield select()
    const { value, type, depth } = state.search
    let endpoint = agent.BacklinkChecker.get
    if (appMode.preview) {
      endpoint = mocked.BacklinkChecker.get
    }
    const url = convertBacklinkURL(value)
    const parsedDepth = parseInt(depth)

    const payload = {
      backlinkChecker: {
        url,
        filter: type.toLowerCase(),
        depth: isNaN(parsedDepth) ? 500 : parsedDepth
      }
    }

    while (strikes > 0) {
      try {
        const body = yield call(endpoint, payload)
        strikes -= 1
        complete = body.data.attributes.status === 'complete'

        if (complete) {
          yield put(actions.getResultsSuccess(body.data, url, complete))
        } else {
          yield put(actions.getResultsNotReady(body.data, url, complete))
        }
      } catch (error) {
        yield put(actions.getResultsFailure(error))
      }
      // 10s delay between each try
      yield delay(8000)
    }
  } catch (error) {
    yield put(actions.getResultsFailure(error))
  }
}

function* loadPreviousSaga(params) {
  try {
    const { historyId } = params.payload
    yield put(push(`/backlink-checker/history/${historyId}`))

    const [history, body] = yield all([
      call(agent.BacklinkChecker.getHistory),
      call(agent.BacklinkChecker.getPrevious, historyId)
    ])
    const historyItem = history.data.find(item => item.id === historyId)
    const {url, depth, filter} = historyItem?.attributes || {}

    yield put(actions.loadPreviousSuccess(body.data, url, depth, filter))
  } catch (error) {
    yield put(actions.loadPreviousFailure(error))
  }
}

function* getHistorySaga() {
  try {
    const body = yield call(agent.BacklinkChecker.getHistory)
    yield put(actions.getHistorySuccess(body.data))
  } catch (error) {
    yield put(actions.getHistoryFailure(error))
  }
}

function* formError(error) {
  yield put(searchActions.setError(error))
  yield put(actions.getResultsFailure(error))
}

function* validateBacklinkCheckerSearch() {
  const state = yield select()
  const { used, limit } = state.appUsage.backlinkChecker
  const { value, depth } = state.search
  const parsedDepth = parseInt(depth)

  if (limit >= 0 && used && used >= limit) {
    yield call(formError, 'Used monthly limit. Upgrade plan or try again later.')
    return null
  }
  if (!value || !isDomainValid(value)) {
    yield call(formError, 'Please enter a valid URL or Domain')
    return null
  }
  if (isNaN(parsedDepth) || parsedDepth < 500 || parsedDepth > 100000) {
    yield call(formError, 'Please set depth value between 500 to 100000')
    return null
  }

  yield put(appUsageActions.hitBacklinkChecker(depth))
  yield race([
    call(pollGetResults),
    take(actions.getResultsSuccess.type),
    take(actions.getResultsFailure.type)
  ])
}

function* deleteHistoryItemSaga(params) {
  try {
    const { historyId } = params.payload
    const data = yield call(agent.BacklinkChecker.del, historyId)
    yield put(actions.deleteHistoryItemSuccess(data))
  } catch (error) {
    yield put(actions.deleteHistoryItemFailure(error))
  }
}

export default function* backlinkCheckerSagas() {
  yield all([
    takeLatest(actions.getResults.type, validateBacklinkCheckerSearch),
    takeLatest(actions.loadPrevious.type, loadPreviousSaga),
    takeLatest(actions.getHistory.type, getHistorySaga),
    takeLatest(actions.deleteHistoryItem.type, deleteHistoryItemSaga)
  ])
}
